// Faixas de segurança: quando a última medida de um sinal pede que alguém leve
// o número à enfermagem.
//
// Pedido do Ilton (30/09): no hospital público o médico passa uma vez por dia;
// quem está ao lado o tempo todo é a família, que mede com aparelhos próprios,
// e a enfermagem, que decide ou aciona o plantonista. Um alerta que diz
// "confirme com o médico" não tem como ser cumprido. Este diz o que conferir,
// o limite e a quem levar o número.
//
// Não é alvo terapêutico dela nem diagnóstico: é o limite a partir do qual a
// família não deve esperar a próxima medida da equipe. Os limites seguem
// referência brasileira sempre que existe uma, e cada um diz de onde veio.
import type { LeituraDTO, TipoLeitura } from './contratos.ts';

/** 'agora' = chamar a enfermagem já; 'avisar' = conferir e levar o número. */
export type NivelDeSeguranca = 'agora' | 'avisar';

export interface AvisoDeSeguranca {
  /** Tipo + lado ("glicemia_baixa"): identifica a regra que disparou. */
  chave: string;
  nivel: NivelDeSeguranca;
  /** "Glicemia baixa: 61 mg/dL". */
  titulo: string;
  oQueFazer: string;
  fonte: string;
  /** A leitura que disparou — "já levei à enfermagem" vale para ela, não para a próxima. */
  leituraId: string;
  medidoEm: string;
}

/** Medida mais velha que isto não dispara aviso: é foto de outro momento. */
export const VALIDADE_DO_AVISO_MS = 4 * 60 * 60 * 1000;

const n = (v: number) => v.toLocaleString('pt-BR');

type Regra = Omit<AvisoDeSeguranca, 'leituraId' | 'medidoEm'>;

/**
 * O aviso de UMA medida, ou null se ela está dentro da faixa de segurança.
 * A pressão é julgada pelo par: a máxima diz se está baixa; a máxima ou a
 * mínima dizem se está alta.
 */
export function avisoDaMedida(tipo: TipoLeitura, valor: number, diastolica?: number): Regra | null {
  switch (tipo) {
    case 'glicemia':
      if (valor < 70) return {
        chave: 'glicemia_baixa', nivel: 'agora',
        titulo: `Glicemia baixa: ${n(valor)} mg/dL`,
        oQueFazer: 'Abaixo de 70 é hipoglicemia, e a enfermagem corrige na hora. Meça de novo 15 minutos depois da correção.',
        fonte: 'Sociedade Brasileira de Diabetes: hipoglicemia é glicose abaixo de 70 mg/dL.',
      };
      if (valor > 250) return {
        chave: 'glicemia_alta', nivel: 'avisar',
        titulo: `Glicemia alta: ${n(valor)} mg/dL`,
        oQueFazer: 'Mostre o número à enfermagem e pergunte se a prescrição tem correção com insulina. Meça de novo 1 a 2 horas depois.',
        fonte: 'Sociedade Brasileira de Diabetes: no hospital, a meta costuma ficar entre 140 e 180 mg/dL.',
      };
      return null;
    case 'pa_sistolica': {
      const par = diastolica !== undefined ? `${n(valor)} x ${n(diastolica)}` : n(valor);
      if (valor < 90) return {
        chave: 'pressao_baixa', nivel: 'avisar',
        titulo: `Pressão baixa: ${par} mmHg`,
        oQueFazer: 'Meça de novo em 5 minutos, com ela deitada e o braço apoiado. Se a máxima continuar abaixo de 90, mostre à enfermagem.',
        fonte: 'Referência usual: pressão baixa é a máxima abaixo de 90 mmHg.',
      };
      if (valor >= 180 || (diastolica ?? 0) >= 120) return {
        chave: 'pressao_alta', nivel: 'avisar',
        titulo: `Pressão alta: ${par} mmHg`,
        oQueFazer: 'Deixe-a 5 minutos em repouso e meça de novo. Se continuar assim, mostre à enfermagem.',
        fonte: 'Diretrizes Brasileiras de Hipertensão (SBC, 2020): 180 x 120 ou mais pede avaliação.',
      };
      return null;
    }
    case 'saturacao':
      if (valor < 92) return {
        chave: 'saturacao_baixa', nivel: valor < 88 ? 'agora' : 'avisar',
        titulo: `Saturação baixa: ${n(valor)}%`,
        oQueFazer: 'Confira o sensor (dedo limpo, mão aquecida, onda regular na tela) e meça de novo. Se continuar abaixo de 92, chame a enfermagem.',
        fonte: 'Referência usual em adulto sem doença pulmonar crônica: saturação de 92% a 96% ou mais.',
      };
      return null;
    case 'temperatura':
      if (valor >= 37.8) return {
        chave: 'febre', nivel: 'avisar',
        titulo: `Febre: ${n(valor)} °C`,
        oQueFazer: 'Mostre à enfermagem: febre pode mudar a conduta (exames, antitérmico). Meça de novo em 1 hora.',
        fonte: 'ANVISA, critérios de infecção relacionada à assistência: febre a partir de 37,8 °C.',
      };
      if (valor < 35) return {
        chave: 'temperatura_baixa', nivel: 'avisar',
        titulo: `Temperatura baixa: ${n(valor)} °C`,
        oQueFazer: 'Cubra e aqueça, e meça de novo em 30 minutos. Se continuar abaixo de 35, mostre à enfermagem.',
        fonte: 'Referência usual: hipotermia é temperatura abaixo de 35 °C.',
      };
      return null;
    case 'frequencia_cardiaca':
      if (valor > 120) return {
        chave: 'fc_alta', nivel: 'agora',
        titulo: `Batimento acelerado: ${n(valor)} bpm`,
        oQueFazer: 'Confira se a onda do oxímetro está regular e meça de novo. Se continuar acima de 120, chame a enfermagem agora: em quem usa marca-passo ou já teve arritmia, não espere.',
        fonte: 'Referência usual: adulto em repouso, de 60 a 100 bpm.',
      };
      if (valor < 50) return {
        chave: 'fc_baixa', nivel: 'agora',
        titulo: `Batimento lento: ${n(valor)} bpm`,
        oQueFazer: 'Meça de novo. Se continuar abaixo de 50, chame a enfermagem agora: com marca-passo, pode ser problema no aparelho.',
        fonte: 'Referência usual: adulto em repouso, de 60 a 100 bpm.',
      };
      return null;
    default:
      return null;
  }
}

/**
 * Os avisos da tela: um por sinal, olhando só a ÚLTIMA medida de cada um.
 * Medida nova dentro da faixa apaga o aviso da anterior; medida velha não
 * dispara; medida de teste (ehReferencia) não conta. Os de 'agora' vêm antes.
 */
export function avisosDeSeguranca(ultimas: LeituraDTO[], agora = Date.now()): AvisoDeSeguranca[] {
  const porTipo = new Map<TipoLeitura, LeituraDTO>();
  for (const l of ultimas) {
    const atual = porTipo.get(l.tipo);
    if (!atual || new Date(l.medidoEm).getTime() > new Date(atual.medidoEm).getTime()) porTipo.set(l.tipo, l);
  }
  const avisos: AvisoDeSeguranca[] = [];
  for (const l of porTipo.values()) {
    if (l.tipo === 'pa_diastolica' || l.ehReferencia) continue;
    if (agora - new Date(l.medidoEm).getTime() > VALIDADE_DO_AVISO_MS) continue;
    const dia = l.tipo === 'pa_sistolica' ? porTipo.get('pa_diastolica') : undefined;
    // A mínima só vale se for do mesmo evento (mesmo grupo ou a mesma hora).
    const mesmoEvento = dia && ((l.grupo && dia.grupo === l.grupo) || dia.medidoEm === l.medidoEm);
    const r = avisoDaMedida(l.tipo, Number(l.valor), mesmoEvento ? Number(dia.valor) : undefined);
    if (r) avisos.push({ ...r, leituraId: l.id, medidoEm: l.medidoEm });
  }
  return avisos.sort((a, b) => (a.nivel === b.nivel ? 0 : a.nivel === 'agora' ? -1 : 1));
}
